/**
 * pptx.js — Export Jubly presentation slides to a .pptx file using pptxgenjs.
 *
 * Usage:
 *   import { exportToPptx } from '../../utils/pptx';
 *   await exportToPptx(slides, title);
 *
 * Each slide is an object with:
 *   { id, background, shapes: [ { type, x, y, w, h, text, fontSize, bold, italic,
 *                                  color, fill, borderColor, borderWidth,
 *                                  rotation, opacity, src } ] }
 *
 * Slide canvas dimensions used in the editor: 960 × 540 px (16:9).
 * pptxgenjs works in inches by default; we map px → inches at 96 dpi.
 */

import PptxGenJS from 'pptxgenjs';

// Editor canvas size in px
const CANVAS_W_PX = 960;
const CANVAS_H_PX = 540;

// pptxgenjs slide size in inches (standard 16:9 = 10 × 5.625")
const SLIDE_W_IN = 10;
const SLIDE_H_IN = 5.625;

/** Convert a pixel X coordinate to inches */
const pxToInX = (px) => (px / CANVAS_W_PX) * SLIDE_W_IN;
/** Convert a pixel Y coordinate to inches */
const pxToInY = (py) => (py / CANVAS_H_PX) * SLIDE_H_IN;
/** Convert a pixel width to inches */
const pxToInW = (pw) => (pw / CANVAS_W_PX) * SLIDE_W_IN;
/** Convert a pixel height to inches */
const pxToInH = (ph) => (ph / CANVAS_H_PX) * SLIDE_H_IN;

/**
 * Normalise a CSS colour string to a 6-char hex string (no #).
 * pptxgenjs requires hex without the leading #.
 * Falls back to '000000' for unknown formats.
 */
function toHex(color) {
  if (!color) return null;
  // Already a 6-char hex with #
  const hexMatch = color.match(/^#([0-9a-fA-F]{6})$/);
  if (hexMatch) return hexMatch[1].toUpperCase();
  // 3-char hex
  const hex3 = color.match(/^#([0-9a-fA-F]{3})$/);
  if (hex3) {
    return hex3[1].split('').map(c => c + c).join('').toUpperCase();
  }
  // rgb(r, g, b)
  const rgb = color.match(/^rgb\(\s*(\d+),\s*(\d+),\s*(\d+)\s*\)$/);
  if (rgb) {
    return [rgb[1], rgb[2], rgb[3]]
      .map(n => parseInt(n).toString(16).padStart(2, '0'))
      .join('').toUpperCase();
  }
  // Named colours — map the most common ones
  const named = {
    white: 'FFFFFF', black: '000000', red: 'FF0000', green: '008000',
    blue: '0000FF', yellow: 'FFFF00', orange: 'FFA500', purple: '800080',
    pink: 'FFC0CB', gray: '808080', grey: '808080', transparent: null,
  };
  const lower = color.toLowerCase().trim();
  if (lower in named) return named[lower];
  return '000000';
}

/**
 * Map a Jubly shape type to a pptxgenjs shape name.
 * pptxgenjs shape names: 'rect', 'ellipse', 'triangle', 'line', etc.
 */
function shapeTypeToPptx(type) {
  switch (type) {
    case 'circle':    return 'ellipse';
    case 'triangle':  return 'triangle';
    case 'diamond':   return 'diamond';
    case 'star':      return 'star5Point';
    case 'arrow':     return 'rightArrow';
    case 'line':      return 'line';
    default:          return 'rect'; // text, rect, image all use rect container
  }
}

/**
 * Add a single Jubly shape to a pptxgenjs slide.
 */
function addShapeToSlide(pptxSlide, shape) {
  const x = pxToInX(shape.x ?? 0);
  const y = pxToInY(shape.y ?? 0);
  const w = pxToInW(shape.w ?? 100);
  const h = pxToInH(shape.h ?? 60);
  const rotate = shape.rotation ?? 0;

  const fillHex  = toHex(shape.fill);
  const colorHex = toHex(shape.color || '#000000');
  const borderHex = toHex(shape.borderColor);

  // ── Image shapes ────────────────────────────────────────────────────────────
  if (shape.type === 'image' && shape.src) {
    try {
      pptxSlide.addImage({
        path: shape.src,
        x, y, w, h,
        rotate,
      });
    } catch {
      // If the image URL is inaccessible, fall back to a placeholder rect
      pptxSlide.addShape('rect', {
        x, y, w, h,
        fill: { color: 'CCCCCC' },
        line: { color: '999999', width: 1 },
      });
    }
    return;
  }

  // ── Line shapes ─────────────────────────────────────────────────────────────
  if (shape.type === 'line') {
    pptxSlide.addShape('line', {
      x, y, w, h,
      line: {
        color: borderHex || colorHex || '000000',
        width: shape.borderWidth ?? 2,
      },
      rotate,
    });
    return;
  }

  // ── Text shapes ─────────────────────────────────────────────────────────────
  if (shape.type === 'text') {
    const textOpts = {
      x, y, w, h,
      fontSize: shape.fontSize ?? 18,
      bold: shape.bold ?? false,
      italic: shape.italic ?? false,
      color: colorHex || '000000',
      align: shape.align ?? 'left',
      valign: 'middle',
      wrap: true,
      rotate,
    };
    if (fillHex) {
      textOpts.fill = { color: fillHex };
    }
    if (borderHex) {
      textOpts.line = { color: borderHex, width: shape.borderWidth ?? 1 };
    }
    pptxSlide.addText(shape.text ?? '', textOpts);
    return;
  }

  // ── Geometric shapes (rect, circle, triangle, diamond, star, arrow) ─────────
  const pptxShapeName = shapeTypeToPptx(shape.type);
  const shapeOpts = {
    x, y, w, h,
    fill: fillHex ? { color: fillHex } : { type: 'none' },
    line: borderHex
      ? { color: borderHex, width: shape.borderWidth ?? 1 }
      : { type: 'none' },
    rotate,
  };

  // If the shape also has text, add it as a text box on top
  pptxSlide.addShape(pptxShapeName, shapeOpts);

  if (shape.text && shape.text.trim()) {
    pptxSlide.addText(shape.text, {
      x, y, w, h,
      fontSize: shape.fontSize ?? 16,
      bold: shape.bold ?? false,
      italic: shape.italic ?? false,
      color: colorHex || '000000',
      align: shape.align ?? 'center',
      valign: 'middle',
      wrap: true,
      rotate,
    });
  }
}

/**
 * Main export function.
 *
 * @param {Array}  slides  — Array of slide objects from the Yjs document
 * @param {string} title   — Presentation title (used as the filename)
 */
export async function exportToPptx(slides, title = 'Presentation') {
  const pptx = new PptxGenJS();

  // Set presentation metadata
  pptx.title   = title;
  pptx.subject = 'Exported from Jubly';
  pptx.author  = 'Jubly';

  // Slide dimensions: 10" × 5.625" (standard widescreen 16:9)
  pptx.defineLayout({ name: 'LAYOUT_WIDE', width: SLIDE_W_IN, height: SLIDE_H_IN });
  pptx.layout = 'LAYOUT_WIDE';

  if (!slides || slides.length === 0) {
    // Always include at least one slide
    pptx.addSlide();
  } else {
    for (const slide of slides) {
      const pptxSlide = pptx.addSlide();

      // ── Slide background ──────────────────────────────────────────────────
      const bg = slide.background;
      if (bg) {
        const bgHex = toHex(bg);
        if (bgHex) {
          pptxSlide.background = { color: bgHex };
        } else if (bg.startsWith('http') || bg.startsWith('/') || bg.startsWith('data:')) {
          // Background image URL
          try {
            pptxSlide.background = { path: bg };
          } catch {
            // ignore inaccessible background images
          }
        }
      }

      // ── Shapes ────────────────────────────────────────────────────────────
      const shapes = slide.shapes ?? [];
      for (const shape of shapes) {
        try {
          addShapeToSlide(pptxSlide, shape);
        } catch (err) {
          console.warn('[pptx] Skipped shape due to error:', err.message, shape);
        }
      }
    }
  }

  // ── Trigger download ──────────────────────────────────────────────────────
  // pptxgenjs writeFile() in browser mode returns a Blob; we create an
  // object URL and click a hidden <a> tag to trigger the native Save dialog.
  const fileName = `${title.replace(/[^a-z0-9_\-\s]/gi, '').trim() || 'presentation'}.pptx`;

  // Use 'blob' output type so it works in both browser and Node environments
  const blob = await pptx.write({ outputType: 'blob' });

  const url = URL.createObjectURL(blob);
  const a   = document.createElement('a');
  a.href     = url;
  a.download = fileName;
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();

  // Clean up after a short delay
  setTimeout(() => {
    URL.revokeObjectURL(url);
    document.body.removeChild(a);
  }, 1000);
}
